import {
    CRITICALITY_LEVELS,
    DAY_PRESETS,
    type DateRange,
    SEARCH_FIELDS,
    TYPE_OPTIONS,
    getStepLabelId,
    matchDayPreset,
    toRangeInputs,
} from '../trackingUtils';
import type { OccurrenceState } from '../types';
import type { TrackingChip } from '../ui/TrackingChips';
import useMtText from '../ui/useMtText';
import { CLEAR_RANGE, type TrackingFilters } from './useTrackingFilters';

const useTrackingChips = (occurrences: OccurrenceState, filters: TrackingFilters) => {
    const { t, msg, formatDateTime } = useMtText();
    const {
        searchByChassis,
        setCriticalityFilter,
        typeFilter,
        setStartDateFilter,
        startDateFilter,
        setUpdateDateFilter,
        updateDateFilter,
        setIsAlertFilter,
        regionalFilter,
        cellFilter,
        cpvNameFilter,
        stepFilter,
        setStepFilter,
        stepTypeCurrentFilter,
        setStepTypeCurrentFilter,
        searchFieldName,
        searchFieldValue,
    } = occurrences;
    const { criticality, period, more, drawerValue, applyMoreFilters, withStageReset } = filters;

    const rangeLabel = (range: DateRange | null) => {
        const inputs = toRangeInputs(range);
        const preset = DAY_PRESETS.find(item => item.key === matchDayPreset(inputs.from, inputs.to));
        if (preset) {
            return msg(preset.labelId);
        }
        const format = (value: string) => (value ? formatDateTime(`${value}T00:00:00`).split(' ')[0] : '…');
        return `${format(inputs.from)} → ${format(inputs.to)}`;
    };

    const chips: TrackingChip[] = [];
    if (searchFieldValue) {
        const field = SEARCH_FIELDS.find(item => item.value === searchFieldName);
        chips.push({
            key: 'search',
            label: `${msg(field?.labelId)}: ${searchFieldValue}`,
            onRemove: () => searchByChassis('', searchFieldName),
        });
    }
    if (criticality) {
        chips.push({
            key: 'criticality',
            label: `${msg('intl-msg:kanban.filterRow.criticality')}: ${msg(
                CRITICALITY_LEVELS.find(level => level.value === criticality)?.labelId
            )}`,
            onRemove: () => setCriticalityFilter('undefined'),
        });
    }
    if (drawerValue.alert !== 'all') {
        chips.push({
            key: 'alert',
            label:
                drawerValue.alert === 'true'
                    ? msg('intl-msg:kanban.filterRow.inAlert')
                    : `${msg('intl-msg:kanban.filterRow.inAlert')}: ${msg('intl-msg:kanban.filterRow.no')}`,
            onRemove: () => setIsAlertFilter('undefined'),
        });
    }
    if (startDateFilter?.start || startDateFilter?.end) {
        chips.push({
            key: 'open',
            label:
                period && period !== 'all'
                    ? `${msg('intl-msg:kanban.filterRow.openingDate')}: ${t(`period.${period}`)}`
                    : `${msg('intl-msg:kanban.filterRow.openingDate')}: ${rangeLabel(startDateFilter)}`,
            onRemove: () => setStartDateFilter(CLEAR_RANGE),
        });
    }
    if (stepTypeCurrentFilter) {
        chips.push({
            key: 'stage',
            label: `${t('table.currentStep')}: ${msg(getStepLabelId(stepTypeCurrentFilter))}`,
            onRemove: () => setStepTypeCurrentFilter(null),
        });
    }
    if (regionalFilter.length) {
        chips.push({
            key: 'regional',
            label: `${msg('intl-msg:kanban.filterRow.regional')}: ${regionalFilter.join(', ')}`,
            onRemove: () => applyMoreFilters({ ...drawerValue, regionals: [], cells: [] }),
        });
    }
    if (cellFilter.length) {
        chips.push({
            key: 'cell',
            label: `${msg('intl-msg:kanban.filterRow.cell')}: ${cellFilter.join(', ')}`,
            onRemove: () => applyMoreFilters({ ...drawerValue, cells: [] }),
        });
    }
    if (cpvNameFilter) {
        chips.push({ key: 'cpv', label: cpvNameFilter, onRemove: () => applyMoreFilters({ ...drawerValue, cpv: '' }) });
    }
    if (more.dealerships.length) {
        chips.push({
            key: 'dealership',
            label: `${msg('intl-msg:kanban.filterRow.dealerships')}: ${more.dealerships.map(item => item.name).join(', ')}`,
            onRemove: () => applyMoreFilters({ ...drawerValue, dealerships: [] }),
        });
    }
    if (more.modelTypes.length || more.models.length) {
        chips.push({
            key: 'models',
            label: `${msg('intl-msg:kanban.filterRow.categoryModel')}: ${t('chips.selected', {
                count: more.modelTypes.length + more.models.length,
            })}`,
            onRemove: () => applyMoreFilters({ ...drawerValue, modelTypes: [], models: [] }),
        });
    }
    if (typeFilter.length) {
        chips.push({
            key: 'types',
            label: `${msg('intl-msg:kanban.filterRow.type')}: ${typeFilter
                .map(type => msg(TYPE_OPTIONS.find(option => option.value === type)?.labelId))
                .join(', ')}`,
            onRemove: () => applyMoreFilters({ ...drawerValue, types: [] }),
        });
    }
    if (more.clients.length) {
        chips.push({
            key: 'clients',
            label: `${msg('intl-msg:kanban.filterRow.customer')}: ${
                more.clients.length > 2 ? t('chips.selected', { count: more.clients.length }) : more.clients.join(', ')
            }`,
            onRemove: () => applyMoreFilters({ ...drawerValue, clients: [] }),
        });
    }
    if (updateDateFilter?.start || updateDateFilter?.end) {
        chips.push({
            key: 'update',
            label: `${msg('intl-msg:kanban.filterRow.updateDate')}: ${rangeLabel(updateDateFilter)}`,
            onRemove: () => setUpdateDateFilter(CLEAR_RANGE),
        });
    }
    if (stepFilter) {
        chips.push({
            key: 'passed',
            label: `${t('filters.passedStep')}: ${msg(getStepLabelId(stepFilter))}`,
            onRemove: () => setStepFilter(null),
        });
    }

    const moreFiltersCount =
        [
            regionalFilter.length,
            cellFilter.length,
            cpvNameFilter,
            more.dealerships.length,
            typeFilter.length,
            more.clients.length,
            stepFilter,
        ].filter(Boolean).length +
        (more.modelTypes.length || more.models.length ? 1 : 0) +
        (updateDateFilter?.start || updateDateFilter?.end ? 1 : 0) +
        (drawerValue.alert === 'false' ? 1 : 0);

    return {
        chips: chips.map(chip => (chip.key === 'stage' ? chip : { ...chip, onRemove: withStageReset(chip.onRemove) })),
        moreFiltersCount,
    };
};

export default useTrackingChips;
